import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, Compass, RotateCcw } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { StepProgress } from "@/components/fionova/StepProgress";
import { EllieBridge } from "@/components/journey/EllieBridge";
import { journey } from "@/lib/journey";
import { prepareFinale } from "@/lib/journeyFinale";
import { Eyebrow, PrimaryButton } from "@/components/fionova/Primitives";
import { purposeNext, purposeReflection } from "@/services/unsaidService";
import { unsaidStore, transcriptHash } from "@/lib/unsaidStore";
import { onboardingState } from "@/lib/onboardingState";
import { trackSessionOnce } from "@/lib/analytics";

const TOTAL = 5;

export default function PurposeEngine() {
  const navigate = useNavigate();
  const [outcome] = useState(() => unsaidStore.getOutcome());
  const [xrayOutcome] = useState(() => unsaidStore.getXrayOutcome());
  const [planOutcome] = useState(() => unsaidStore.getPlanOutcome());
  const [futureOutcome] = useState(() => unsaidStore.getFutureOutcome());

  const hash = transcriptHash(outcome?.transcript || []);
  const restored = useRef(unsaidStore.getPurposeChat(hash));
  const r0 = restored.current;

  const [phase, setPhase] = useState(() => r0?.phase || "intro"); // intro | finding | generating | reflection
  const [history, setHistory] = useState(() => r0?.history || []);
  const [step, setStep] = useState(() => r0?.step || 0);
  const [insight, setInsight] = useState(() => r0?.insight || { noticing: "", why_next: "" });
  const [question, setQuestion] = useState(() => r0?.question || "");
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reflection, setReflection] = useState(() => unsaidStore.getPurposeOutcome()?.hash === hash ? unsaidStore.getPurposeOutcome().reflection : null);
  const [reflectError, setReflectError] = useState(false);
  const startedRef = useRef((r0?.history || []).length > 0);

  const context = {
    name: onboardingState.getAccount()?.name || "",
    unsaid: { observations: outcome?.observations || [], feedback: outcome?.feedback || "", handoff: outcome?.handoff || {} },
    xray: xrayOutcome?.xray || {},
    plan: planOutcome?.plan || {},
    future_self_conversation: (futureOutcome?.messages || []).map((m) => ({ who: m.role === "user" ? "present" : "future", text: m.text })),
    onboarding: onboardingState.getSetup() || {},
  };

  useEffect(() => {
    if (phase === "finding") {
      try { sessionStorage.setItem("fionova_purpose_chat", JSON.stringify({ hash, history, step, insight, question, phase })); } catch {}
    }
  }, [history, step, insight, question, phase, hash]);

  const runQuestion = async (base) => {
    setFailed(false);
    setThinking(true);
    const res = await purposeNext(context, base);
    setThinking(false);
    if (!res.ok) { setFailed(true); return; }
    if (res.done) { revealReflection(base); return; }
    setInsight({ noticing: res.noticing, why_next: res.why_next });
    setQuestion(res.question);
    setHistory([...base, { role: "ellie", text: res.question }]);
    setStep((s) => s + 1);
  };

  const start = async () => {
    if (startedRef.current && history.length > 0) { setPhase("finding"); return; }
    startedRef.current = true;
    setPhase("finding");
    await runQuestion([]);
  };

  const answer = async (text) => {
    const content = (text ?? draft).trim();
    if (!content || thinking) return;
    const base = [...history, { role: "user", text: content }];
    setHistory(base);
    setDraft("");
    await runQuestion(base);
  };

  const retry = () => runQuestion(history);

  const revealReflection = async (hist) => {
    setReflectError(false);
    setPhase("generating");
    const { ok, reflection: rf } = await purposeReflection(context, hist || history);
    if (!ok) { setReflectError(true); return; }
    setReflection(rf);
    unsaidStore.savePurposeOutcome({
      hash, reflection: rf,
      memory: { statement: rf.statement, strengths: rf.strengths || [], purpose_step: rf.purpose_step || null },
    });
    unsaidStore.appendHistory({ type: "purpose", hash, reflection: rf, conversation: hist || history });
    setPhase("reflection");
  };

  // Prepare the closing reflection in the background while the Purpose reveal is read.
  useEffect(() => {
    if (phase === "reflection" && reflection && journey.isOn() && !reflection.crisis) prepareFinale();
  }, [phase, reflection]);

  // ---- Intro ----
  if (phase === "intro") {
    return (
      <OnboardingLayout testId="page-purpose" contentClassName="justify-center py-10">
        <StepProgress current={5} className="mb-10" />
        <EllieBridge feature="purpose" className="mb-8" />
        <div className="flex flex-col items-center text-center">
          <motion.span initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}
            className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Compass className="h-8 w-8" strokeWidth={1.5} />
          </motion.span>
          <Eyebrow className="mt-8">Purpose Engine · Finding your pattern</Eyebrow>
          <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
            Now, let's find the pattern.
          </h1>
          <div className="mt-5 space-y-3 text-base leading-relaxed text-muted-foreground">
            <p>You've looked at what you're carrying and where you're headed. Now we look for something different — the patterns that point toward what gives your life meaning.</p>
            <p>I'll ask a few short questions. After each one, I'll show you what I'm noticing — and why I'm asking the next.</p>
            <p className="font-medium text-foreground">There are no right answers. Short answers are completely fine.</p>
          </div>
        </div>
        <div className="mt-12">
          <PrimaryButton data-testid="purpose-begin" onClick={start}>Begin</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // ---- Generating ----
  if (phase === "generating") {
    return (
      <OnboardingLayout testId="page-purpose" contentClassName="items-center justify-center py-10 text-center">
        {!reflectError ? (
          <>
            <div className="flex items-center gap-1.5" data-testid="purpose-generating">
              {[0, 1, 2].map((i) => (<span key={i} className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />))}
            </div>
            <p className="mt-6 text-base text-muted-foreground">Bringing your pattern together…</p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-4" data-testid="purpose-reflect-error">
            <p className="max-w-sm text-base leading-relaxed text-muted-foreground">Let me take another moment to gather this.</p>
            <button data-testid="purpose-reflect-retry" onClick={() => revealReflection(history)}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-all hover:-translate-y-0.5 hover:border-primary/40">
              <RotateCcw className="h-4 w-4" /> Try again
            </button>
          </div>
        )}
      </OnboardingLayout>
    );
  }

  // ---- Reflection (concise) ----
  if (phase === "reflection" && reflection) {
    const rf = reflection;
    const Card = ({ label, children, testId, delay }) => (
      <motion.div data-testid={testId} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay }}
        className="rounded-3xl bg-card p-5 ring-1 ring-border">
        <p className="eyebrow text-[11px] text-muted-foreground">{label}</p>
        <div className="mt-2 text-base leading-relaxed text-foreground/90">{children}</div>
      </motion.div>
    );
    return (
      <div data-testid="page-purpose" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
        <div className="mx-auto flex min-h-screen w-full max-w-[520px] flex-col px-6 py-10 sm:px-8">
          <StepProgress current={5} className="mb-8" />
          <Eyebrow className="text-center">Your Purpose Reflection</Eyebrow>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mt-5 rounded-3xl bg-primary/[0.07] p-6 ring-1 ring-primary/30" data-testid="purpose-statement">
            <p className="eyebrow text-[11px] text-primary">What your purpose may be pointing toward</p>
            <p className="mt-2 font-heading text-2xl font-medium leading-snug tracking-tight text-foreground">{rf.statement}</p>
          </motion.div>

          <div className="mt-4 flex flex-col gap-4">
            {rf.surfacing && <Card testId="purpose-surfacing" label="What keeps surfacing" delay={0.1}>{rf.surfacing}</Card>}
            {rf.pattern && <Card testId="purpose-pattern" label="The pattern" delay={0.16}>{rf.pattern}</Card>}
            {Array.isArray(rf.strengths) && rf.strengths.length > 0 && (
              <Card testId="purpose-strengths" label="Strengths Ellie sees" delay={0.22}>
                <div className="flex flex-wrap gap-2">
                  {rf.strengths.map((s, i) => (
                    <span key={i} className="rounded-full bg-background px-3.5 py-1.5 text-sm font-medium text-foreground ring-1 ring-border">{s}</span>
                  ))}
                </div>
              </Card>
            )}
            {rf.recognition && <Card testId="purpose-recognition" label="Something you may not have noticed" delay={0.19}>{rf.recognition}</Card>}
            {(rf.values?.length > 0 || rf.motivations) && (
              <Card testId="purpose-values" label="What you value & what moves you" delay={0.2}>
                {rf.values?.length > 0 && <div className="mb-2 flex flex-wrap gap-2">{rf.values.map((v, i) => (
                  <span key={i} className="rounded-full bg-primary/[0.08] px-3.5 py-1.5 text-sm font-medium text-foreground">{v}</span>))}</div>}
                {rf.motivations && <p>{rf.motivations}</p>}
              </Card>
            )}
            {rf.directions?.length > 0 && (
              <Card testId="purpose-directions" label="Where this could lead" delay={0.26}>
                <ul className="space-y-1.5">{rf.directions.map((d, i) => <li key={i} className="flex gap-2"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{d}</li>)}</ul>
              </Card>
            )}
            {rf.purpose_step?.action && (
              <motion.div data-testid="purpose-step" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.3 }}
                className="rounded-3xl bg-primary/[0.07] p-5 ring-1 ring-primary/40">
                <p className="eyebrow text-[11px] text-primary">Your Purpose Step</p>
                <p className="mt-2 text-base font-medium leading-relaxed text-foreground">{rf.purpose_step.action}</p>
              </motion.div>
            )}
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 rounded-3xl bg-card/70 p-5 text-center ring-1 ring-border" data-testid="purpose-complete-note">
            <p className="font-medium text-foreground">You've completed all five parts of your journey.</p>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">Take your time with this. When you're ready, I'll bring everything we explored together into one closing reflection.</p>
          </motion.div>
          <div className="mt-6">
            <PrimaryButton data-testid="purpose-continue" onClick={() => {
              trackSessionOnce("step_purpose", "onboarding_step_completed", { step_name: "purpose_engine", step_number: 5 });
              navigate(journey.isOn() ? "/journey/complete" : "/app");
            }}>{journey.isOn() ? "See my closing reflection" : "Continue"}</PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  // ---- Finding (guided pattern questions) ----
  const canContinue = !!draft.trim() && !thinking;
  return (
    <OnboardingLayout testId="page-purpose" contentClassName="py-8">
      <StepProgress current={5} className="mb-6" />
      <div className="flex items-center gap-2" data-testid="purpose-progress">
        <Compass className="h-4 w-4 text-primary" />
        <span className="eyebrow text-[11px] text-primary">Finding your pattern · {Math.min(step, TOTAL)} of {TOTAL}</span>
      </div>

      <div className="mt-6 flex-1">
        <AnimatePresence mode="wait">
          <motion.div key={step + (thinking ? "-t" : "") + (failed ? "-f" : "")}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
            {thinking ? (
              <div className="flex items-center gap-1.5 py-8" data-testid="purpose-thinking">
                {[0, 1, 2].map((i) => (<span key={i} className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />))}
              </div>
            ) : failed ? (
              <div className="flex flex-col items-start gap-3 py-6" data-testid="purpose-retry">
                <p className="text-sm leading-relaxed text-muted-foreground">The line went quiet for a moment. Let's reconnect.</p>
                <button data-testid="purpose-retry-btn" onClick={retry}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-all hover:-translate-y-0.5 hover:border-primary/40">
                  <RotateCcw className="h-4 w-4" /> Let me try that again
                </button>
              </div>
            ) : (
              <>
                {insight.noticing && (
                  <div className="mb-6 rounded-3xl bg-card p-5 ring-1 ring-border" data-testid="purpose-insight">
                    <p className="eyebrow text-[11px] text-primary">What I'm noticing</p>
                    <p className="mt-1.5 text-base leading-relaxed text-foreground/90">{insight.noticing}</p>
                    {insight.why_next && (
                      <>
                        <p className="eyebrow mt-4 text-[11px] text-muted-foreground">Why I'm asking this next</p>
                        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{insight.why_next}</p>
                      </>
                    )}
                  </div>
                )}
                <h1 data-testid="purpose-question" className="text-2xl font-semibold leading-snug tracking-tight text-foreground">
                  {question}
                </h1>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {!thinking && !failed && (
        <div className="mt-6">
          <div className="flex items-end gap-2 rounded-3xl bg-card p-2 ring-1 ring-border focus-within:ring-2 focus-within:ring-primary">
            <textarea data-testid="purpose-input" rows={1} value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); answer(); } }}
              placeholder="A sentence or two is plenty…"
              className="max-h-40 flex-1 resize-none bg-transparent px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none" />
            <button data-testid="purpose-send" onClick={() => answer()} disabled={!canContinue}
              aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-all enabled:hover:bg-primary/90 disabled:opacity-40">
              <ArrowUp className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </OnboardingLayout>
  );
}
